import { Menu, Search, ShoppingBag, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { storefrontApi } from '../lib/api'
import type { NavigationLink } from '../types/commerce'
import { Brand } from './Brand'

const fallbackLinks: NavigationLink[] = [
  { id: 'fallback-new', label: 'NOVIDADES', href: '/produtos?sort=newest', position: 10 },
  { id: 'fallback-roupas', label: 'ROUPAS', href: '/produtos', position: 20 },
  { id: 'fallback-acessorios', label: 'ACESSÓRIOS', href: '/produtos?category=acessorios', position: 30 },
  { id: 'fallback-featured', label: 'DESTAQUES', href: '/produtos?sort=featured', position: 40 }
]

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [links, setLinks] = useState<NavigationLink[]>(fallbackLinks)
  const navigate = useNavigate()
  const cart = useCart()
  const searchInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let mounted = true
    storefrontApi.navigation()
      .then(items => {
        if (mounted) setLinks(items.length ? items : fallbackLinks)
      })
      .catch(() => {
        if (mounted) setLinks(fallbackLinks)
      })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    if (!menuOpen && !searchOpen) return
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const panel = document.getElementById(searchOpen ? 'search-panel' : 'menu-panel')
    const focusable = () => Array.from(panel?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled)') ?? [])
    if (searchOpen) searchInput.current?.focus()
    else focusable()[0]?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); setSearchOpen(false) }
      if (event.key !== 'Tab') return
      const elements = focusable()
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
      previousFocus?.focus()
    }
  }, [menuOpen, searchOpen])

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = term.trim()
    if (!q) return
    navigate(`/produtos?search=${encodeURIComponent(q)}`)
    setSearchOpen(false)
    setTerm('')
  }

  const leftLinks = links.slice(0, 2)
  const rightLinks = links.slice(2, 4)

  return (
    <>
      <header className="site-header">
        <button className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Abrir menu"><Menu /></button>

        <nav className="desktop-nav desktop-nav--left" aria-label="Navegação principal">
          <Link to="/">INÍCIO</Link>
          {leftLinks.map(item => <Link key={item.id} to={item.href}>{item.label}</Link>)}
        </nav>

        <div className="site-header__brand"><Brand /></div>

        <div className="site-header__right">
          <nav className="desktop-nav desktop-nav--right" aria-label="Atalhos da loja">
            {rightLinks.map(item => <Link key={item.id} to={item.href}>{item.label}</Link>)}
          </nav>
          <div className="site-header__actions">
            <button className="icon-button" onClick={() => setSearchOpen(true)} aria-label="Pesquisar"><Search /></button>
            <button className="icon-button bag-button" onClick={() => cart.setOpen(true)} aria-label="Abrir sacola">
              <ShoppingBag />
              {cart.count > 0 && <span className="bag-count">{cart.count}</span>}
            </button>
          </div>
        </div>
      </header>

      <div id="menu-panel" role="dialog" aria-modal={menuOpen || undefined} aria-label="Menu da loja" className={`overlay-panel ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <div className="overlay-panel__top">
          <button className="icon-button" onClick={() => setMenuOpen(false)} aria-label="Fechar menu"><X /></button>
          <Brand />
          <span className="overlay-spacer" />
        </div>
        <div className="menu-links">
          <Link to="/" onClick={() => setMenuOpen(false)}>INÍCIO</Link>
          {links.map(item => (
            <Link key={item.id} to={item.href} onClick={() => setMenuOpen(false)}>{item.label}</Link>
          ))}
        </div>
        <div className="menu-footer-links">
          <Link to="/admin" onClick={() => setMenuOpen(false)}>ADMIN</Link>
          <Link to="/contato" onClick={() => setMenuOpen(false)}>CONTATO</Link>
        </div>
      </div>

      <div id="search-panel" role="dialog" aria-modal={searchOpen || undefined} aria-label="Pesquisar produtos" aria-hidden={!searchOpen} className={`search-overlay ${searchOpen ? 'is-open' : ''}`}>
        <button className="icon-button search-overlay__close" onClick={() => setSearchOpen(false)} aria-label="Fechar pesquisa"><X /></button>
        <form onSubmit={submitSearch} className="search-form">
          <label htmlFor="site-search">O QUE VOCÊ PROCURA?</label>
          <div className="search-form__row">
            <input ref={searchInput} id="site-search" type="search" enterKeyHint="search" aria-label="Buscar produtos" value={term} onChange={e => setTerm(e.target.value)} placeholder="Digite sua busca" />
            <button type="submit" className="button button--dark">BUSCAR</button>
          </div>
        </form>
      </div>
    </>
  )
}
